import { Loader2, AlertTriangle, CalendarClock, ListTodo, Flame } from "lucide-react";
import { useTarefasDashboard } from "@/hooks/useTarefasDashboard";
import {
  TAREFA_PRIORIDADE_COLOR,
  TAREFA_STATUS_LABEL,
  type Tarefa,
  type TarefasView,
} from "@/types/tarefas";
import { formatPrazoCurto, isPrazoAtrasado } from "./tarefasUtils";
import { Badge } from "@/components/ui/badge";

type Props = {
  onNavigate: (view: TarefasView) => void;
  onOpenTarefa: (tarefa: Tarefa) => void;
};

function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  onClick,
}: {
  label: string;
  value: number;
  hint?: string;
  icon: typeof ListTodo;
  tone?: "default" | "danger" | "warn" | "urgent";
  onClick?: () => void;
}) {
  const tones = {
    default: "from-fynix-mist to-fynix-brand text-white",
    danger: "from-red-500 to-red-700 text-white",
    warn: "from-amber-400 to-amber-600 text-white",
    urgent: "from-orange-400 to-orange-600 text-white",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${tones[tone]} p-5 text-left min-h-[120px]`}
    >
      <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-white/15" />
      <Icon className="h-5 w-5 opacity-80" />
      <p className="mt-3 text-3xl font-semibold tracking-tight">{value}</p>
      <p className="text-sm opacity-90">{label}</p>
      {hint && <p className="mt-1 text-xs opacity-70">{hint}</p>}
    </button>
  );
}

function ListaMini({
  titulo,
  itens,
  empty,
  onOpen,
}: {
  titulo: string;
  itens: Tarefa[];
  empty: string;
  onOpen: (t: Tarefa) => void;
}) {
  return (
    <div className="crm-card p-4">
      <h3 className="crm-title text-base mb-3">{titulo}</h3>
      <div className="space-y-2">
        {itens.length === 0 ? (
          <p className="text-sm text-fynix-muted py-6 text-center">{empty}</p>
        ) : (
          itens.slice(0, 6).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onOpen(t)}
              className="w-full text-left rounded-xl border border-fynix-line px-3 py-2.5 hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium line-clamp-1">{t.titulo}</p>
                <Badge
                  variant="outline"
                  className={`shrink-0 text-[10px] ${TAREFA_PRIORIDADE_COLOR[t.prioridade]}`}
                >
                  {t.prioridade}
                </Badge>
              </div>
              <div className="mt-1 flex items-center gap-2 text-[11px] text-fynix-muted">
                <span className={isPrazoAtrasado(t) ? "text-red-600 font-medium" : ""}>
                  {formatPrazoCurto(t.prazo)}
                </span>
                <span>·</span>
                <span>{t.responsavel?.nome ?? "—"}</span>
                {t.cliente?.nome && (
                  <>
                    <span>·</span>
                    <span className="truncate">{t.cliente.nome}</span>
                  </>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export function TarefasHome({ onNavigate, onOpenTarefa }: Props) {
  const {
    isLoading,
    abertas,
    minhasAbertas,
    atrasadas,
    hoje,
    proximas,
    porStatus,
    recentes,
    urgentes,
  } = useTarefasDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-fynix-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Minhas tarefas"
          value={minhasAbertas}
          hint={`${abertas} abertas no total`}
          icon={ListTodo}
          onClick={() => onNavigate("lista")}
        />
        <KpiCard
          label="Hoje"
          value={hoje.length}
          hint="Com prazo para hoje"
          icon={CalendarClock}
          tone="warn"
          onClick={() => onNavigate("calendario")}
        />
        <KpiCard
          label="Urgente"
          value={urgentes}
          hint="Prioridade urgente"
          icon={Flame}
          tone="urgent"
          onClick={() => onNavigate("kanban")}
        />
        <KpiCard
          label="Vencidas"
          value={atrasadas.length}
          hint="Prazo já passou"
          icon={AlertTriangle}
          tone="danger"
          onClick={() => onNavigate("lista")}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ListaMini
          titulo="Vencidas"
          itens={atrasadas}
          empty="Nada vencido — ótimo!"
          onOpen={onOpenTarefa}
        />
        <ListaMini
          titulo="Hoje"
          itens={hoje}
          empty="Sem tarefas para hoje"
          onOpen={onOpenTarefa}
        />
        <ListaMini
          titulo="Próximos 7 dias"
          itens={proximas}
          empty="Agenda leve nos próximos dias"
          onOpen={onOpenTarefa}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-5 crm-card p-4">
          <h3 className="crm-title text-base mb-3">Por status</h3>
          <div className="space-y-2">
            {porStatus.map((s) => (
              <div key={s.status} className="flex items-center justify-between text-sm">
                <span className="text-fynix-ink-soft">{s.label}</span>
                <span className="font-semibold tabular-nums">{s.total}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="xl:col-span-7 crm-card p-4">
          <h3 className="crm-title text-base mb-3">Atualizadas recentemente</h3>
          <div className="space-y-2">
            {recentes.length === 0 ? (
              <p className="text-sm text-fynix-muted py-6 text-center">
                Crie a primeira tarefa para começar
              </p>
            ) : (
              recentes.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpenTarefa(t)}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-fynix-line px-3 py-2 text-left hover:bg-muted/40"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{t.titulo}</p>
                    <p className="text-[11px] text-fynix-muted">
                      {TAREFA_STATUS_LABEL[t.status]} · {formatPrazoCurto(t.prazo)}
                    </p>
                  </div>
                  <span className="text-[11px] text-fynix-muted shrink-0">
                    {t.responsavel?.nome?.split(" ")[0]}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
